import React, { useCallback, useState } from 'react';
import { PaintBucket, Save, RefreshCw, Download, Calculator } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import SurfaceParametersSection from './SurfaceParametersSection';
import RoomParametersSection from './RoomParametersSection';
import PaintParametersSection from './PaintParametersSection';
import AdditionalParametersSection from './AdditionalParametersSection';

/**
 * Компонент основной формы расчета расхода краски
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  roomTypes,
  surfaceTypes,
  paintTypes,
  paintQualities,
  surfaceConditions,
  saveCalculation 
}) => {
  
  // Состояние для имени сохраняемого расчета
  const [saveCalculationName, setSaveCalculationName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Сбросим состояния ошибок при изменении
      if (errors[name]) {
        setErrors(prevErrors => {
          const newErrors = { ...prevErrors };
          delete newErrors[name];
          return newErrors;
        });
      }

      // Если меняется тип расчета, обновим связанные поля
      if (name === 'calculationType') {
        // Если выбран расчет по площади, нет необходимости в размерах комнаты
        if (value === 'area') {
          // Ничего не меняем
        } else if (value === 'room') {
          // Автоматически рассчитаем площадь из размеров комнаты
          const roomLength = parseFloatOrDefault(prev.roomLength, 0);
          const roomWidth = parseFloatOrDefault(prev.roomWidth, 0);
          const roomArea = roomLength * roomWidth;
          if (roomArea > 0) {
            newState.surfaceArea = roomArea.toString();
          }
        }
      }

      // Если меняются параметры комнаты, пересчитаем площадь
      if ((name === 'roomLength' || name === 'roomWidth') && prev.calculationType === 'room') {
        const roomLength = name === 'roomLength' ? parseFloatOrDefault(value, 0) : parseFloatOrDefault(prev.roomLength, 0);
        const roomWidth = name === 'roomWidth' ? parseFloatOrDefault(value, 0) : parseFloatOrDefault(prev.roomWidth, 0);
        const roomArea = roomLength * roomWidth;
        if (roomArea > 0) {
          newState.surfaceArea = roomArea.toString();
        }
      }

      // Если меняется тип комнаты, можем предложить подходящий тип краски
      if (name === 'roomType') {
        if (value === 'bathroom' || value === 'kitchen') {
          // Для влажных помещений рекомендуем влагостойкие краски
          newState.paintType = 'latex';
        } else if (value === 'children') {
          // Для детской экологичные краски
          newState.paintType = 'water';
        }
      }

      // Если меняется тип поверхности, можем предложить подходящий тип краски
      if (name === 'surfaceType') {
        if (value === 'wood') {
          newState.paintType = 'alkyd';
        } else if (value === 'metal') {
          newState.paintType = 'alkyd';
        }
      }

      return newState;
    });
  }, [errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      surfaceArea, roomType, surfaceType, paintType, paintQuality, coatsNumber,
      wallsHeight, ceilingIncluded, calculationType,
      roomLength, roomWidth, windowsNumber, windowHeight, windowWidth,
      doorsNumber, doorHeight, doorWidth, pricePerLiter
    } = formData;

    // Парсинг числовых значений
    const numSurfaceArea = parseFloatOrDefault(surfaceArea, -1);
    const numCoatsNumber = parseIntOrDefault(coatsNumber, -1);
    const numWallsHeight = parseFloatOrDefault(wallsHeight, -1);
    const numRoomLength = parseFloatOrDefault(roomLength, -1);
    const numRoomWidth = parseFloatOrDefault(roomWidth, -1);
    const numWindowsNumber = parseIntOrDefault(windowsNumber, -1);
    const numWindowHeight = parseFloatOrDefault(windowHeight, -1);
    const numWindowWidth = parseFloatOrDefault(windowWidth, -1);
    const numDoorsNumber = parseIntOrDefault(doorsNumber, -1);
    const numDoorHeight = parseFloatOrDefault(doorHeight, -1);
    const numDoorWidth = parseFloatOrDefault(doorWidth, -1);
    const numPricePerLiter = parseFloatOrDefault(pricePerLiter, -1);

    // Валидация базовых полей
    if (numSurfaceArea <= 0) newErrors.surfaceArea = 'Площадь должна быть положительной.';
    if (!roomType) newErrors.roomType = 'Выберите тип помещения.';
    if (!surfaceType) newErrors.surfaceType = 'Выберите тип поверхности.';
    if (!paintType) newErrors.paintType = 'Выберите тип краски.';
    if (!paintQuality) newErrors.paintQuality = 'Выберите качество краски.';
    
    if (numCoatsNumber <= 0) newErrors.coatsNumber = 'Укажите количество слоев.';
    if (numCoatsNumber > 5) newErrors.coatsNumber = 'Слишком много слоев (макс. 5).';
    
    // Для расчета по параметрам комнаты
    if (calculationType === 'room') {
      if (numWallsHeight <= 0) newErrors.wallsHeight = 'Высота стен должна быть положительной.';
      if (numWallsHeight > 6) newErrors.wallsHeight = 'Высота стен не более 6 метров.';
      
      if (numRoomLength <= 0) newErrors.roomLength = 'Длина комнаты должна быть положительной.';
      if (numRoomLength > 30) newErrors.roomLength = 'Длина комнаты не более 30 метров.';
      
      if (numRoomWidth <= 0) newErrors.roomWidth = 'Ширина комнаты должна быть положительной.';
      if (numRoomWidth > 30) newErrors.roomWidth = 'Ширина комнаты не более 30 метров.';
      
      // Валидация окон
      if (numWindowsNumber < 0) newErrors.windowsNumber = 'Количество окон не может быть отрицательным.';
      if (numWindowsNumber > 20) newErrors.windowsNumber = 'Слишком много окон (макс. 20).';
      
      if (numWindowsNumber > 0) {
        if (numWindowHeight <= 0) newErrors.windowHeight = 'Высота окна должна быть положительной.';
        if (numWindowHeight > 3) newErrors.windowHeight = 'Высота окна не более 3 метров.';
        
        if (numWindowWidth <= 0) newErrors.windowWidth = 'Ширина окна должна быть положительной.';
        if (numWindowWidth > 3) newErrors.windowWidth = 'Ширина окна не более 3 метров.';
      }
      
      // Валидация дверей
      if (numDoorsNumber < 0) newErrors.doorsNumber = 'Количество дверей не может быть отрицательным.';
      if (numDoorsNumber > 10) newErrors.doorsNumber = 'Слишком много дверей (макс. 10).';
      
      if (numDoorsNumber > 0) {
        if (numDoorHeight <= 0) newErrors.doorHeight = 'Высота двери должна быть положительной.';
        if (numDoorHeight > 3) newErrors.doorHeight = 'Высота двери не более 3 метров.';
        
        if (numDoorWidth <= 0) newErrors.doorWidth = 'Ширина двери должна быть положительной.';
        if (numDoorWidth > 2) newErrors.doorWidth = 'Ширина двери не более 2 метров.';
      }
    }
    
    // Валидация цены
    if (numPricePerLiter < 0) newErrors.pricePerLiter = 'Цена не может быть отрицательной.';
    if (numPricePerLiter > 10000) newErrors.pricePerLiter = 'Цена слишком высокая (макс. 10000₽).';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета расхода краски
  const calculatePaintUsage = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const surfaceArea = parseFloatOrDefault(formData.surfaceArea);
        const coatsNumber = parseIntOrDefault(formData.coatsNumber);
        const wallsHeight = parseFloatOrDefault(formData.wallsHeight);
        const roomLength = parseFloatOrDefault(formData.roomLength);
        const roomWidth = parseFloatOrDefault(formData.roomWidth);
        const windowsNumber = parseIntOrDefault(formData.windowsNumber);
        const windowHeight = parseFloatOrDefault(formData.windowHeight);
        const windowWidth = parseFloatOrDefault(formData.windowWidth);
        const doorsNumber = parseIntOrDefault(formData.doorsNumber);
        const doorHeight = parseFloatOrDefault(formData.doorHeight);
        const doorWidth = parseFloatOrDefault(formData.doorWidth);
        const pricePerLiter = parseFloatOrDefault(formData.pricePerLiter);
        const includeBaseboard = formData.includeBaseboard;
        const baseboardLength = parseFloatOrDefault(formData.baseboardLength);
        const includeStucco = formData.includeStucco;
        const stuccoLength = parseFloatOrDefault(formData.stuccoLength);
        const ceilingIncluded = formData.ceilingIncluded;
        const calculationType = formData.calculationType;

        // Получаем параметры типа краски и поверхности
        const selectedPaintType = paintTypes.find(pt => pt.id === formData.paintType) || paintTypes[0];
        const selectedSurfaceType = surfaceTypes.find(st => st.id === formData.surfaceType) || surfaceTypes[0];
        const selectedQuality = paintQualities.find(pq => pq.id === formData.paintQuality) || paintQualities[1];
        const selectedCondition = surfaceConditions.find(sc => sc.id === formData.surfaceCondition) || surfaceConditions[1];
        
        // --- Расчет площади поверхности ---
        let totalArea = surfaceArea;
        let wallsArea = 0;
        let ceilingArea = 0;
        let windowsArea = 0;
        let doorsArea = 0;
        
        // Если расчет по параметрам комнаты
        if (calculationType === 'room') {
          // Расчет площади стен
          const perimeter = 2 * (roomLength + roomWidth);
          wallsArea = perimeter * wallsHeight;
          
          // Расчет площади потолка (и пола, если нужно)
          ceilingArea = roomLength * roomWidth;
          
          // Расчет площади окон
          windowsArea = windowsNumber * windowHeight * windowWidth;
          
          // Расчет площади дверей
          doorsArea = doorsNumber * doorHeight * doorWidth;
          
          // Общая площадь для покраски стен
          let paintableWallsArea = wallsArea - windowsArea - doorsArea;
          
          // Если включен потолок, добавляем его площадь
          totalArea = ceilingIncluded ? paintableWallsArea + ceilingArea : paintableWallsArea;
        }
        
        // --- Расчет расхода краски ---
        
        // Базовый расход краски (на 1 м² при одном слое)
        let baseCoverage = selectedPaintType.coverage; // м²/л - сколько квадратных метров покрывает 1 литр
        
        // Корректировка расхода в зависимости от качества краски
        const qualityAdjustedCoverage = baseCoverage * selectedQuality.coverageModifier;
        
        // Корректировка расхода в зависимости от поверхности
        const surfaceAdjustedCoverage = qualityAdjustedCoverage / selectedSurfaceType.absorption;
        
        // Корректировка расхода в зависимости от состояния поверхности
        const finalCoverage = surfaceAdjustedCoverage / selectedCondition.absorptionModifier;
        
        // Расчет количества краски с учетом всех слоев
        const totalPaintVolume = (totalArea * coatsNumber) / finalCoverage;
        
        // Расчет расхода для плинтусов и лепнины (если применимо)
        let additionalVolume = 0;
        if (includeBaseboard && baseboardLength > 0) {
          // Примерный расход на погонный метр плинтуса
          const baseboardCoveragePerMeter = 0.05; // 0.05 литра на метр плинтуса
          additionalVolume += baseboardLength * baseboardCoveragePerMeter * coatsNumber;
        }
        
        if (includeStucco && stuccoLength > 0) {
          // Примерный расход на погонный метр лепнины
          const stuccoCoveragePerMeter = 0.07; // 0.07 литра на метр лепнины
          additionalVolume += stuccoLength * stuccoCoveragePerMeter * coatsNumber;
        }
        
        // Итоговый расход краски
        const finalPaintVolume = totalPaintVolume + additionalVolume;
        
        // Расчет стоимости
        const totalCost = finalPaintVolume * pricePerLiter;
        
        // Расчет рекомендуемого объема с запасом 
        // (обычно рекомендуется брать с запасом 10-15%)
        const recommendedVolume = finalPaintVolume * 1.15;
        
        // Расчет рекомендуемого количества банок стандартных объемов
        const standardSizes = [0.9, 2.7, 5, 10]; // стандартные объемы банок в литрах
        
        const recommendedCans = standardSizes.map(size => {
          const cansCount = Math.ceil(recommendedVolume / size);
          const totalVolume = cansCount * size;
          const totalCansCost = totalVolume * pricePerLiter;
          
          return {
            size,
            count: cansCount,
            totalVolume,
            cost: totalCansCost
          };
        });
        
        // Находим оптимальный вариант (наименьшая переплата)
        const optimalCans = [...recommendedCans].sort((a, b) => {
          // Сортируем по наименьшей разнице между необходимым объемом и общим объемом банок
          const diffA = a.totalVolume - recommendedVolume;
          const diffB = b.totalVolume - recommendedVolume;
          return diffA - diffB;
        })[0];
        
        // --- Готовим итоговые результаты ---
        const finalResults = {
          totalArea,
          wallsArea: calculationType === 'room' ? wallsArea : null,
          ceilingArea: calculationType === 'room' && ceilingIncluded ? ceilingArea : null,
          windowsArea: calculationType === 'room' ? windowsArea : null,
          doorsArea: calculationType === 'room' ? doorsArea : null,
          
          paintType: selectedPaintType.name,
          paintQuality: selectedQuality.name,
          surfaceType: selectedSurfaceType.name,
          surfaceCondition: selectedCondition.name,
          
          coveragePerLiter: finalCoverage,
          coatsNumber,
          
          exactPaintVolume: finalPaintVolume,
          recommendedVolume,
          
          pricePerLiter,
          totalCost,
          
          recommendedCans,
          optimalCans,
          
          includeBaseboard,
          baseboardLength: includeBaseboard ? baseboardLength : 0,
          includeStucco,
          stuccoLength: includeStucco ? stuccoLength : 0,
          
          calculationDate: new Date()
        };
        
        setResults(finalResults);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, paintTypes, surfaceTypes, paintQualities, surfaceConditions, errors, validateForm, setErrors, setIsCalculating, setResults]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      surfaceArea: '50',
      roomType: 'living',
      surfaceType: 'drywall',
      paintType: 'water',
      paintQuality: 'medium',
      coatsNumber: '2',
      wallsHeight: '2.7',
      ceilingIncluded: true,
      surfaceCondition: 'normal',
      calculationType: 'area',
      roomLength: '5',
      roomWidth: '4',
      windowsNumber: '2',
      windowHeight: '1.5',
      windowWidth: '1.2',
      doorsNumber: '1',
      doorHeight: '2.0',
      doorWidth: '0.9',
      includeBaseboard: false,
      baseboardLength: '18',
      includeStucco: false,
      stuccoLength: '0',
      pricePerLiter: '350',
    });
    setResults(null);
    setErrors({});
    setSaveCalculationName('');
    setShowSaveDialog(false);
  };

  // Экспорт результатов в CSV
  const exportToCSV = () => {
    if (!results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Формируем строки с данными
    const rows = [
      ['Калькулятор расхода краски - Результаты расчета'],
      ['Дата расчета:', new Date().toLocaleDateString('ru-RU')],
      [''],
      ['Параметры расчета:'],
      ['Общая площадь покраски:', `${results.totalArea.toFixed(2)} м²`],
    ];

    // Добавляем параметры комнаты, если расчет по комнате
    if (results.wallsArea !== null) {
      rows.push(['Площадь стен:', `${results.wallsArea.toFixed(2)} м²`]);
      if (results.ceilingArea !== null) {
        rows.push(['Площадь потолка:', `${results.ceilingArea.toFixed(2)} м²`]);
      }
      rows.push(['Площадь окон:', `${results.windowsArea.toFixed(2)} м²`]);
      rows.push(['Площадь дверей:', `${results.doorsArea.toFixed(2)} м²`]);
    }

    // Добавляем параметры краски
    rows.push(['']);
    rows.push(['Тип краски:', results.paintType]);
    rows.push(['Качество краски:', results.paintQuality]);
    rows.push(['Тип поверхности:', results.surfaceType]);
    rows.push(['Состояние поверхности:', results.surfaceCondition]);
    rows.push(['Количество слоев:', results.coatsNumber.toString()]);
    rows.push(['']);
    rows.push(['Результаты:']);
    rows.push(['Расход краски (точный):', `${results.exactPaintVolume.toFixed(2)} л`]);
    rows.push(['Рекомендуемый объем (с запасом):', `${results.recommendedVolume.toFixed(2)} л`]);
    rows.push(['Цена за литр:', `${results.pricePerLiter.toFixed(2)} ₽`]);
    rows.push(['Стоимость краски:', `${results.totalCost.toFixed(2)} ₽`]);
    rows.push(['']);
    rows.push(['Рекомендуемое количество банок:']);
    
    // Добавляем рекомендации по банкам
    results.recommendedCans.forEach(can => {
      rows.push([
        `Банки по ${can.size} л:`,
        `${can.count} шт. (общий объем: ${can.totalVolume.toFixed(2)} л, стоимость: ${can.cost.toFixed(2)} ₽)`
      ]);
    });
    
    rows.push(['']);
    rows.push(['Оптимальный вариант:']);
    rows.push([
      `Банки по ${results.optimalCans.size} л:`,
      `${results.optimalCans.count} шт. (общий объем: ${results.optimalCans.totalVolume.toFixed(2)} л, стоимость: ${results.optimalCans.cost.toFixed(2)} ₽)`
    ]);

    // Обработка дополнительных элементов
    if (results.includeBaseboard || results.includeStucco) {
      rows.push(['']);
      rows.push(['Дополнительные элементы:']);
      if (results.includeBaseboard) {
        rows.push(['Плинтус:', `${results.baseboardLength.toFixed(2)} м`]);
      }
      if (results.includeStucco) {
        rows.push(['Лепнина/молдинги:', `${results.stuccoLength.toFixed(2)} м`]);
      }
    }

    // Формируем CSV строку
    const csvContent = rows.map(row => row.join(';')).join('\n');
    
    // Создаем и скачиваем файл
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'расчет_расхода_краски.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения расчета
  const handleSaveCalculation = () => {
    if (showSaveDialog) {
      const savedResult = saveCalculation(saveCalculationName || null);
      if (savedResult) {
        alert(`Расчет "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveCalculationName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculatePaintUsage();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <PaintBucket className="w-6 h-6 text-indigo-500" />
          Параметры расчета
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Выбор типа расчета */}
        <div className="mb-6">
          <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Как будем считать площадь?
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="calculationType"
                value="area"
                checked={formData.calculationType === 'area'}
                onChange={handleInputChange}
                className="form-radio"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Знаю площадь поверхности
              </span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="calculationType"
                value="room"
                checked={formData.calculationType === 'room'}
                onChange={handleInputChange}
                className="form-radio"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                По размерам помещения
              </span>
            </label>
          </div>
        </div>

        {/* Параметры поверхности */}
        <SurfaceParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          surfaceTypes={surfaceTypes}
          surfaceConditions={surfaceConditions}
        />

        {/* Параметры помещения (если выбран расчет по комнате) */}
        {formData.calculationType === 'room' && (
          <RoomParametersSection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            errors={errors} 
            roomTypes={roomTypes}
          />
        )}

        {/* Параметры краски */}
        <PaintParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          paintTypes={paintTypes}
          paintQualities={paintQualities}
        />

        {/* Дополнительные параметры */}
        <AdditionalParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Диалог сохранения расчета */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveCalculationName}
                onChange={(e) => setSaveCalculationName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveCalculation}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Calculator className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать расход краски'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать результаты (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveCalculation}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;